import type { ApiClient } from './client';

/**
 * The organization's people, as `veri-plus-backend` serves them.
 *
 * `routes/v1/organization.routes.ts`: `GET /v1/organization/members` and
 * `GET /v1/organization/invitations`, pagination only, answering
 * `{ members, nextCursor }` and `{ invitations, nextCursor }`, newest first;
 * `POST /v1/organization/invitations` with `{ email, role }`, answering 201
 * `{ invitation }`; `DELETE /v1/organization/members/:userId`, answering 204.
 * Every one is behind `requireOrgAdmin`, so a member gets 403
 * `insufficient_role`. A member is `user.model.ts` through its `toJSON`, an
 * invitation is `invitation.model.ts` through its own, without the token.
 *
 * Inviting an address that already has an open invitation replaces it, so
 * the board's Resend is the same POST again. Removing the last admin is 409
 * `last_admin`. D15.
 */

export type TeamRole = 'org_admin' | 'org_member';

export interface Member {
  id: string;
  email: string;
  name: string;
  role: TeamRole;
  organizationId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Invitation {
  id: string;
  organizationId: string;
  email: string;
  role: TeamRole;
  invitedBy: string;
  expiresAt: string;
  acceptedAt?: string;
  /** True until accepted. */
  open: boolean;
  createdAt: string;
  updatedAt: string;
}

/** The API's own ceiling, `paginationQuery` in `validation/common.ts`. */
const pageLimit = 100;

/** Follows `nextCursor` until it runs out. A cursor that repeats ends the walk. */
async function walk<T>(
  api: ApiClient,
  path: string,
  key: string,
  signal?: AbortSignal,
): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | null = null;
  const seen = new Set<string>();
  do {
    const query: string = cursor === null ? '' : `&cursor=${encodeURIComponent(cursor)}`;
    const page = await api.get<Record<string, unknown>>(
      `${path}?limit=${pageLimit}${query}`,
      signal ? { signal } : {},
    );
    items.push(...((page[key] as T[] | undefined) ?? []));
    cursor = (page.nextCursor as string | null | undefined) ?? null;
    if (cursor !== null) {
      if (seen.has(cursor)) break;
      seen.add(cursor);
    }
  } while (cursor !== null);
  return items;
}

export const membersPath = '/v1/organization/members';
export const invitationsPath = '/v1/organization/invitations';

export function listAllMembers(api: ApiClient, signal?: AbortSignal): Promise<Member[]> {
  return walk<Member>(api, membersPath, 'members', signal);
}

export function listAllInvitations(api: ApiClient, signal?: AbortSignal): Promise<Invitation[]> {
  return walk<Invitation>(api, invitationsPath, 'invitations', signal);
}

export async function invite(api: ApiClient, email: string, role: TeamRole): Promise<Invitation> {
  const answer = await api.post<{ invitation: Invitation }>(invitationsPath, { email, role });
  return answer.invitation;
}

export async function removeMember(api: ApiClient, userId: string): Promise<void> {
  await api.delete<unknown>(`${membersPath}/${encodeURIComponent(userId)}`);
}

/** Open and not yet expired: what the board lists as pending. */
export function isPending(invitation: Invitation, now: Date): boolean {
  return invitation.open && new Date(invitation.expiresAt).getTime() > now.getTime();
}

const day = 24 * 60 * 60 * 1000;

/** `expires in 6 days`, rounded up, so a code with hours left still reads `in 1 day`. */
export function expiresIn(invitation: Invitation, now: Date): string {
  const days = Math.max(
    1,
    Math.ceil((new Date(invitation.expiresAt).getTime() - now.getTime()) / day),
  );
  return `expires in ${days} ${days === 1 ? 'day' : 'days'}`;
}

/**
 * The board's order: the signed-in user, then admins, then members, each in
 * the API's own order. D15.
 */
export function teamOrder(members: Member[], selfId: string): Member[] {
  const rank = (m: Member) => (m.id === selfId ? 0 : m.role === 'org_admin' ? 1 : 2);
  return members
    .map((m, i) => ({ m, i }))
    .sort((a, b) => rank(a.m) - rank(b.m) || a.i - b.i)
    .map((x) => x.m);
}

/**
 * The invitee field's placeholder: the board writes `name@` and the domain of
 * the admin's own address beside it, so this takes the signed-in user's.
 */
export function placeholderFor(email: string): string {
  const at = email.lastIndexOf('@');
  return at < 0 ? 'name@' : `name@${email.slice(at + 1)}`;
}
